import { graphql, HeadProps, useStaticQuery } from "gatsby"
import React from "react"

import { i18n } from "../../locales/lingui"
import { ensureTrailingSlash } from "../../utils/anchor-links"
import { LOCALES } from "../../utils/locales/constants"
import {
  getLocaleFromPathname,
  getLocalizedPagePath,
  getNonLocalizedPathname,
} from "../../utils/locales/locales"
import { CanonicalTag } from "./canonical-tag"
import { HreflangTag } from "./hreflang-tag"
import { JsonContentMarkup } from "./json-content-markup"
import { usePageTracking } from "./use-page-tracking"
import { getItemListElementFromPath } from "./utils"

type Props = {
  title: string
  description?: string
  image?: string
  nonCrawlable?: boolean
  location: HeadProps["location"]
  /** Used for customizing alternate canonical value. Leaving it blank will default to the regular pathname, which is what we want on most cases */
  canonical?: string
}

export const FALLBACK_SEO_IMAGE =
  "https://www.datocms-assets.com/85985/1707936079-tremendous-open-graph-img-2x.jpg"

const Seo = ({
  description,
  title,
  image = FALLBACK_SEO_IMAGE,
  nonCrawlable = false,
  location,
  canonical,
}: Props) => {
  const pageLocale = getLocaleFromPathname(location.pathname)
  // we activate here, sometimes superfluously
  // otherwise, we'd need to activate on every hardcoded page regardless
  // of whether it's available in other locales
  i18n.activate(pageLocale)

  usePageTracking(title, location.pathname)

  const nonLocalizedPathname = getNonLocalizedPathname(location.pathname)

  const { site } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          author
        }
      }
    }
  `)

  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = site.siteMetadata?.title

  return (
    <>
      <title>{title || defaultTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content="website" />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={site.siteMetadata?.author || ``} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      <CanonicalTag pathname={canonical ?? location.pathname} />
      {nonCrawlable && <meta name="robots" content="noindex" />}

      <JsonContentMarkup
        content={{
          "@type": "Organization",
          "@id": "https://www.tremendous.com/",
          name: "Tremendous",
          url: "https://www.tremendous.com/",
          logo: "https://www.datocms-assets.com/85985/1669659740-tremendous-logo.svg",
          description:
            description ||
            i18n._(
              "Send digital payments internationally at scale. Instant delivery, frustration-free redemption, hundreds of reward options. Buy, send, track, manage and brand your payouts on our easy-to-use platform.",
            ),
          sameAs: [
            "https://www.linkedin.com/company/tremendous-rewards/",
            "https://twitter.com/gotremendous",
            "https://www.youtube.com/@tremendousrewards",
          ],
        }}
      />
      {location && (
        <JsonContentMarkup
          content={{
            "@type": "BreadcrumbList",
            itemListElement: getItemListElementFromPath(location.pathname),
          }}
        />
      )}
      {LOCALES.reduce<React.ReactNode[]>((acc, locale) => {
        const path = getLocalizedPagePath(nonLocalizedPathname, locale)
        if (path) {
          acc.push(
            <HreflangTag
              key={locale}
              pathname={ensureTrailingSlash(path)}
              locale={locale}
            />,
          )
        }
        return acc
      }, [])}
    </>
  )
}

export default Seo
